// ---------------------------------------------------------------------------
// Beyond Grants page (/funding/beyond-grants).
// From the master strategy brief. Educational content about financing
// approaches - no promise of funding availability, returns, liquidity or
// capital preservation anywhere.
// ---------------------------------------------------------------------------

export const beyondGrants = {
  eyebrow: "Funding & Finance",
  title: "Beyond Grants: Designing Finance for Regeneration",
  standfirst:
    "Connecting capital, community assets and regenerative projects to create lasting value for people and place.",
  heroBody: [
    "Regenerative projects need more than good ideas. They need resources that reflect their purpose, scale and long-term ambitions.",
    "Grants and philanthropy are important, but they are not always sufficient to secure land, establish productive enterprises, build shared infrastructure or sustain restoration over generations.",
    "Bioregional Ventures helps organisations develop credible projects and appropriate financing strategies. Through Bioregional Capital Partners, we offer an investment approach focused on preserving and growing capital through productive regenerative assets and enterprises.",
  ],
  heroCta: "Discuss Your Project",

  whyHeading: "Why regenerative projects need different financial approaches",
  whyBody: [
    "Conventional financing often prioritises predictable cash flows, short investment horizons and readily measurable returns. Regenerative initiatives may need substantial early investment while producing ecological and community benefits over many years.",
    "Some projects can generate commercial income; others create public benefits without a direct revenue stream. Effective financing begins with understanding which benefits can support repayment or investment returns, which require grant funding, and how ownership and stewardship will be protected.",
  ],
  pullQuote: "The right finance should strengthen the project, not compromise its purpose.",

  approachesHeading: "Six practical financing approaches",
  approaches: [
    {
      title: "Catalytic funding - make projects ready",
      text: "Early grants, philanthropy or owner-provided development capital can support feasibility studies, land assessments, governance, partnerships, budgets and project documentation. This can prepare a project for larger commitments.",
      role: "Our role: Development planning, feasibility coordination, grant research, project budgets and investment-readiness materials.",
    },
    {
      title: "Patient and concessional capital - recognise longer time horizons",
      text: "Suitable financing may offer longer repayment periods or more flexible terms for enterprises that take time to become commercially established. Possibilities include concessional loans, recoverable grants and other mission-aligned arrangements.",
      role: "Our role: Commercial feasibility, cash-flow projections, funding requirements and coordination with qualified financing specialists. These mechanisms are not guaranteed to be available.",
    },
    {
      title: "Community asset finance - secure land and infrastructure",
      text: "Communities may combine donations, grants, member contributions and suitable repayable finance to acquire or develop land and productive infrastructure. Community land trusts, cooperatives and other ownership models can support long-term stewardship, subject to legal and governance requirements.",
      role: "Our role: Project feasibility, ownership and governance planning, capital mapping and coordination with legal, property and finance advisers.",
    },
    {
      title: "Blended finance - combine complementary sources",
      text: "A project may need non-repayable funding for community engagement and restoration, while commercial or concessional capital finances infrastructure that produces revenue. Blended arrangements must distinguish the purposes, rights, risks and obligations of each source.",
      role: "Our role: Funding strategy, capital sequencing, budgets, proposal preparation and specialist coordination.",
    },
    {
      title: "Regional funding pools and project portfolios - connect initiatives",
      text: "Related projects across a landscape may benefit from coordinated funding strategies, shared infrastructure and collaborative proposals. Potential approaches include aligned grants, pooled philanthropic contributions, independent regranting arrangements and separately governed financing vehicles.",
      role: "Our role: Stakeholder engagement, regional programme design, capital mapping, coordinated project development and early feasibility. Bioregional Ventures does not currently operate a pooled fund.",
    },
    {
      title: "Regenerative enterprise and revenue finance - build enduring income",
      text: "Productive land, seed enterprises, nurseries, regenerative farming, food processing and community infrastructure may generate income through trading, contracts, leases, services, advance purchases or suitable financing arrangements. Environmental markets may be relevant to eligible projects, but involve substantial risks and obligations.",
      role: "Our role: Enterprise feasibility, market assessment, business planning, revenue modelling and project preparation.",
    },
  ],

  scenariosLabel: "Illustrative scenarios",
  scenarios: [
    {
      title: "Illustrative scenario A - community regenerative farm",
      intro:
        "A community group seeks to secure agricultural land for a cooperative farm, education, restoration and shared infrastructure. A possible development and financing sequence is:",
      steps: [
        "Grant or philanthropic funding for feasibility, governance and land assessment.",
        "Establishment of a suitable ownership and decision-making structure.",
        "Community contributions, donations and appropriately structured acquisition finance.",
        "Grants, equity or suitable equipment finance for productive infrastructure.",
        "Farm revenue, memberships, leases or service agreements to sustain operations.",
      ],
      role: "Bioregional Ventures' potential role: Feasibility, development planning, financial modelling, funding strategy, partnerships and implementation support.",
    },
    {
      title: "Illustrative scenario B - regional native seed and restoration programme",
      intro:
        "Several organisations coordinate native seed collection, nurseries, restoration, training and local employment. A possible approach includes:",
      steps: [
        "Public or philanthropic support for regional planning and governance.",
        "Grants for seed collection, training and ecological work.",
        "Enterprise development for seed production and nursery services.",
        "Supply agreements and restoration contracts where commercially viable.",
        "Coordinated funding proposals for shared infrastructure and long-term operations.",
      ],
      role: "Bioregional Ventures' potential role: Regional programme design, capital mapping, funding research, enterprise feasibility, partnerships and delivery planning.",
    },
  ],

  relatedPagesHeading: "Related pages",
  relatedPages: [
    { label: "Preparing projects for funding", href: "/funding/projects" },
    { label: "Our approach", href: "/approach" },
    { label: "Bioregional Capital Partners", href: "/capital-partners" },
  ],

  ctaHeading: "Have a Regenerative Project or Investment Opportunity?",
  ctaBody:
    "We welcome conversations about productive land, regenerative enterprise, community infrastructure and collaborative regional projects. Whether you need project development support or wish to explore a potential financing partnership, the first step is a clear conversation about purpose, feasibility and next steps.",
  ctaButton: "Start a Conversation",
};
